// Edit layer style > Rule > Geometry > Color Scheme > Unique Values

import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import { theme_limeGreen, theme_orange, theme_textColorBlended, theme_textColorMain } from '../../../Theme';
import { ILayerStyle, ILayerStyleRule, ILayerStyleRule_Geometry, ILayerStyleType_UniqueValues, ILayerStyleType_UniqueValuesItem } from './LayerStyleInterfaces';
import { CallServer } from '../../../CallServer';
import Debug from '../../../Debug';
import { ToastNotification } from '../../../ToastNotifications';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { CustomTextField } from '../EditLayer';
import { MuiColorInputStyled } from '../EditLayerStyle';
import { MuiColorInputColors } from 'mui-color-input';
import iwanthue from "iwanthue";
import { ChangeEvent, useState } from 'react';
import { ILayerAttribExpression } from './LayerAttribExpressionInterfaces';
import { TrendingUpRounded } from '@mui/icons-material';


const UNIQUE_VALUES_MAX_ITEMS = 300;

export const RULE_EXPANSION_MAX_ITEMS = 20;


//-------------------------------------------------------------------------------
// Component props
//-------------------------------------------------------------------------------
export interface EditLayerStyle_ColorScheme_UniqueValues_Props
{
  rule: ILayerStyleRule;   // input
  OnRuleUniqueValuesUpdated: any;      // output
  OnExpandRule: any;
}

//-------------------------------------------------------------------------------
// Edit layer style > Rule > Geometry > Color Scheme > Unique Values
//-------------------------------------------------------------------------------
export function EditLayerStyle_ColorScheme_UniqueValues(props: EditLayerStyle_ColorScheme_UniqueValues_Props) 
{


  // Keeps track of which value/color item is being edited.  For performance reasons, only one item 
  // can be edited at a time.
  const [itemBeingEdited, setItemBeingEdited] = useState<ILayerStyleType_UniqueValuesItem|undefined>(undefined);









  //-------------------------------------------------------------------------------
  // A Unique Values color has changed.
  //-------------------------------------------------------------------------------
  function OnUniqueValuesColorChanged(value: string, colors: MuiColorInputColors, 
                                      uniqueValuesItem: ILayerStyleType_UniqueValuesItem)
  {
    if(!props.rule.geometry || !props.rule.geometry.unique_values) return;

    // Create an updated item
    const updated_item: ILayerStyleType_UniqueValuesItem =
    {
      ...uniqueValuesItem,
      color: value,
    }

    const updated_unique_values: ILayerStyleType_UniqueValues = 
    {
      ...props.rule.geometry.unique_values,
      items: props.rule.geometry.unique_values.items.map(oldItem => oldItem.id === uniqueValuesItem.id ? updated_item : oldItem)
    }

    // Notify caller of the change
    props.OnRuleUniqueValuesUpdated(updated_unique_values);
  }

  //-------------------------------------------------------------------------------
  // UNIQUE VALUES Remove item button was pushed.
  //-------------------------------------------------------------------------------
  function OnUniqueValuesRemoveColorItem(uniqueValuesItem: ILayerStyleType_UniqueValuesItem)
  {
    if(!props.rule.geometry || !props.rule.geometry.unique_values) return;

    // Create an updated item

    const updated_unique_values: ILayerStyleType_UniqueValues = 
    {
      ...props.rule.geometry.unique_values,
      items: props.rule.geometry.unique_values.items.filter(oldItem => oldItem.id !== uniqueValuesItem.id)
    }

    // Notify caller of the change
    props.OnRuleUniqueValuesUpdated(updated_unique_values);
  }

  //-------------------------------------------------------------------------------
  // Enter EDIT mode for a specific unique value item.
  //-------------------------------------------------------------------------------
  function OnEnterEditMode(uniqueValuesItem: ILayerStyleType_UniqueValuesItem)
  {
    setItemBeingEdited(uniqueValuesItem);
  }

  //-------------------------------------------------------------------------------
  // Exit EDIT mode.
  //-------------------------------------------------------------------------------
  function OnExitEditMode()
  {
    setItemBeingEdited(undefined);
  }

  //-------------------------------------------------------------------------------
  // UNIQUE VALUES Clear All button was pushed.
  //-------------------------------------------------------------------------------
  async function OnUniqueValuesClearAll()
  {
    if(!props.rule.geometry || !props.rule.geometry.attribute_id) return;

    setItemBeingEdited(undefined);

    const new_unique_values: ILayerStyleType_UniqueValues = 
    {
      items: []
    }

    // Notify caller of the change
    props.OnRuleUniqueValuesUpdated(new_unique_values);
  }

  //-------------------------------------------------------------------------------
  // UNIQUE VALUES Refresh All button was pushed.
  //-------------------------------------------------------------------------------
  async function OnUniqueValuesRefreshAll(randomColors: boolean)
  {
    if(!props.rule.geometry || !props.rule.geometry.attribute_id) return;

    setItemBeingEdited(undefined);

    const new_unique_values: ILayerStyleType_UniqueValues | undefined = await LayerStyle_RegenerateUniqueValues(props.rule.geometry.attribute_id, randomColors);
    if(!new_unique_values)
      return;

    // Notify caller of the change
    props.OnRuleUniqueValuesUpdated(new_unique_values);
  }

  //-------------------------------------------------------------------------------
  // UNIQUE VALUES Update Only button was pushed.
  //-------------------------------------------------------------------------------
  async function OnUniqueValuesUpdateOnly()
  {
    if(!props.rule.geometry || !props.rule.geometry.attribute_id || !props.rule.geometry.unique_values) return;

    setItemBeingEdited(undefined);

    const new_unique_values: ILayerStyleType_UniqueValues | undefined = await LayerStyle_RegenerateUniqueValues(props.rule.geometry.attribute_id, false);
    if(!new_unique_values)
      return;

    // Remove any items whose values no longer exist
    //
    // OLD LIST:  props.rule.geometry.unique_values.items
    // NEW LIST:  new_unique_values

    let new_unique_values_items_updated: ILayerStyleType_UniqueValuesItem[] = [];
    let maxID: number = -1;
    let entriesRemovedCount: number = 0;

    for(let i=0; i < props.rule.geometry.unique_values.items.length; i++)
    {
      const foundInNewUniqueValuesList: ILayerStyleType_UniqueValuesItem | undefined = new_unique_values.items.find(nuv => nuv.value.trim().toLowerCase() === props.rule.geometry?.unique_values?.items[i].value.trim().toLowerCase());
      if(foundInNewUniqueValuesList !== undefined)
      {
        new_unique_values_items_updated.push(props.rule.geometry.unique_values.items[i]);

        // Also keep track of the max ID
        if(props.rule.geometry.unique_values.items[i].id > maxID)
          maxID = props.rule.geometry.unique_values.items[i].id;
      }
      else
        entriesRemovedCount++;
    }

    // Add new items for any missing values

    let entriesAddedCount: number = 0;

    for(let i=0; i < new_unique_values.items.length; i++)
    {
      const foundExistingItem: ILayerStyleType_UniqueValuesItem | undefined = new_unique_values_items_updated.find(nuv => nuv.value.trim().toLowerCase() === new_unique_values.items[i].value.trim().toLowerCase());
      if(foundExistingItem === undefined)
      {
        // This value does not already exist - add it as a new item
        new_unique_values_items_updated.push(
          {
            id: 1+maxID++,
            value: new_unique_values.items[i].value,
            color: ''
          })
        entriesAddedCount++;
      }
    }

    // Re-sort the updated list

    if(props.rule.geometry.attribute_type === 'number')
      new_unique_values_items_updated = new_unique_values_items_updated.sort((a: ILayerStyleType_UniqueValuesItem, b: ILayerStyleType_UniqueValuesItem) => Number.parseFloat(a.value)! - Number.parseFloat(b.value!));
    else if(props.rule.geometry.attribute_type === 'string') // Sort such that empty values are at the end of the array
      new_unique_values_items_updated = new_unique_values_items_updated.sort((a: ILayerStyleType_UniqueValuesItem, b: ILayerStyleType_UniqueValuesItem) => {if (!a.value) return 1; if (!b.value) return -1; return a.value.localeCompare(b.value)});

    // Create an updated item

    const updated_unique_values: ILayerStyleType_UniqueValues = 
    {
      ...props.rule.geometry.unique_values,
      items: new_unique_values_items_updated
    }

    // Notify caller of the change
    props.OnRuleUniqueValuesUpdated(updated_unique_values);

    // Notify user of outcome

    if(entriesRemovedCount === 0 && entriesAddedCount === 0)
      ToastNotification('info', 'No changes needed');
    else if(entriesRemovedCount > 0 && entriesAddedCount === 0)
      ToastNotification('info', `Removed ${entriesRemovedCount} ${entriesRemovedCount===1?'entry':'entries'}`);
    else if(entriesRemovedCount === 0 && entriesAddedCount > 0)
      ToastNotification('info', `Added ${entriesAddedCount} new ${entriesAddedCount===1?'entry':'entries'}`);
    else
      ToastNotification('info', `Removed: ${entriesRemovedCount}  |  Added: ${entriesAddedCount}`);
  }

  //-------------------------------------------------------------------------------
  // UNIQUE VALUES Manually add a new item.
  //-------------------------------------------------------------------------------
  async function OnUniqueValuesAddNewItem()
  {
    if(!props.rule.geometry || !props.rule.geometry.unique_values) return;

    const next_id = props.rule.geometry.unique_values.items.length === 0 ? 1 : props.rule.geometry.unique_values.items.reduce((prev, current) => (prev.id > current.id) ? prev : current).id + 1;

    const new_item: ILayerStyleType_UniqueValuesItem = 
    {
      id: next_id,
      value: '',
      color: ''
    }

    const new_unique_values: ILayerStyleType_UniqueValues = 
    {
      ...props.rule.geometry.unique_values,
      items: [...props.rule.geometry.unique_values.items, new_item ]
    }

    // Put this new item into EDIT mode
    setItemBeingEdited(new_item);

    // Notify caller of the change
    props.OnRuleUniqueValuesUpdated(new_unique_values);
  }  

  //-------------------------------------------------------------------------------
  // UNIQUE VALUES An item's value has changed.
  //-------------------------------------------------------------------------------
  function OnUniqueValueItemTextFieldChanged(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, uniqueValuesItem: ILayerStyleType_UniqueValuesItem): void 
  {
    if(!props.rule.geometry || !props.rule.geometry.unique_values) return;

    const newValue: string = event.target.value;

    // Create an updated item

    const updated_item: ILayerStyleType_UniqueValuesItem = 
    {
      ...uniqueValuesItem,
      value: newValue,
    }

    const updated_unique_values: ILayerStyleType_UniqueValues = 
    {
      ...props.rule.geometry.unique_values,
      items: props.rule.geometry.unique_values.items.map(oldItem => oldItem.id === uniqueValuesItem.id ? updated_item : oldItem)
    }

    // Notify caller of the change
    props.OnRuleUniqueValuesUpdated(updated_unique_values);
  }

  //-------------------------------------------------------------------------------
  // Expand this rule into multiple rules (one rule per item).
  //-------------------------------------------------------------------------------
  async function OnExpandRule()
  {
    props.OnExpandRule(props.rule);
  }











  

  if(!props.rule.geometry || !props.rule.geometry.unique_values || !props.rule.geometry.attribute_id) return null;

  // Main render

  return (

    <Stack sx={{ mx: 0, mt: 2 }}>

      {/* Utility buttons */}

      <Stack direction='row' sx={{ my: 1, alignItems: 'center'}}>

        <Button variant='outlined' sx={{ ml: 0, textTransform: 'none' }} 
                onClick={(_)=>OnUniqueValuesClearAll()}>
          <Stack direction='column'>
            <Typography sx={{ fontSize: '0.8rem', color: theme_textColorBlended }}>
              Clear All
            </Typography>
            <Typography sx={{ fontSize: '0.6rem', color: theme_textColorMain, opacity: 0.5 }}>
              Remove all items
            </Typography>
          </Stack>
        </Button>

        <Button variant='outlined' sx={{ ml: 2, textTransform: 'none' }} 
                onClick={(_)=>OnUniqueValuesRefreshAll(false)}>
          <Stack direction='column'>
            <Typography sx={{ fontSize: '0.8rem', color: theme_textColorBlended }}>
              Refresh All
            </Typography>
            <Typography sx={{ fontSize: '0.6rem', color: theme_textColorMain, opacity: 0.5 }}>
              No colors assigned
            </Typography>
          </Stack>
        </Button>

        <Button variant='outlined' sx={{ ml: 2, textTransform: 'none' }}
                onClick={(_)=>OnUniqueValuesRefreshAll(true)}>
          <Stack direction='column'>
            <Typography sx={{ fontSize: '0.8rem', color: theme_textColorBlended }}>
              Refresh All
            </Typography>
            <Typography sx={{ fontSize: '0.6rem', color: theme_textColorMain, opacity: 0.5 }}>
              Assign random colors
            </Typography>
          </Stack>
        </Button>

        <Button variant='outlined' sx={{ ml: 2, textTransform: 'none' }}
                onClick={(_)=>OnUniqueValuesUpdateOnly()}>
          <Stack direction='column'>
            <Typography sx={{ fontSize: '0.8rem', color: theme_textColorBlended }}>
              Update Only
            </Typography>
            <Typography sx={{ fontSize: '0.6rem', color: theme_textColorMain, opacity: 0.5 }}>
              Remove unused, add missing, re-sort
            </Typography>
          </Stack>
        </Button>

      </Stack>

      {/* Stats */}

      <Stack direction='row' sx={{ my: 1, alignItems: 'center' }}>

        <Stack direction='row' sx={{ alignItems: 'center' }}>
          <Typography sx={{ color: theme_textColorBlended, fontSize: '0.8rem' }}>
            Unique Values:
          </Typography>
          <Typography sx={{ ml: 1, color: theme_limeGreen+'C0', fontSize: '1.0rem' }}>
            {props.rule.geometry.unique_values.items.length}
          </Typography>
          {/* {props.rule.geometry.unique_values.items.length === UNIQUE_VALUES_MAX_ITEMS
            ?
              <Typography sx={{ ml: 1, color: theme_errorRed+'C0', fontSize: '0.8rem' }}>
                (max limit reached - the remaining unique values are not shown)
              </Typography>
            :null
          } */}
        </Stack>

        {props.rule.geometry.attribute_type === 'number'
          ?
            <Stack direction='row' sx={{ ml: 3, alignItems: 'center' }}>
              <Typography sx={{ color: theme_textColorBlended, fontSize: '0.8rem' }}>
                Range:
              </Typography>
              <Typography sx={{ ml: 1, color: theme_limeGreen+'C0', fontSize: '1.0rem' }}>
                {GetNumericalAttribMinValue(props.rule.geometry.unique_values.items)}
              </Typography>
              <Typography sx={{ mx: 1, color: theme_textColorBlended, fontSize: '0.8rem' }}>
                to
              </Typography>
              <Typography sx={{ color: theme_limeGreen+'C0', fontSize: '1.0rem' }}>
                {GetNumericalAttribMaxValue(props.rule.geometry.unique_values.items)}
              </Typography>
            </Stack>
          :null
        }
      </Stack>

      {/* All unique values and their colors */}

      {props.rule.geometry.unique_values.items.map(function(uniqueValuesItem)
      {
        const isNumber: boolean = props.rule.geometry?.attribute_type === 'number';

        if(itemBeingEdited?.id === uniqueValuesItem.id)
        return (

          // EDIT MODE

          <Stack key={uniqueValuesItem.id} direction='row' 
                 sx={{ my: 0.5, width: '100%', alignItems: 'center', bgcolor: theme_textColorBlended+'30', 
                       borderRadius: 2, py: 1, pl: 1 }}>

            {/* Value edit box */}

            <Stack sx={{ width: /*isNumber ? '150px' :*/ '100%' }}>
              <Typography sx={{ color: theme_textColorBlended, fontSize: '0.8rem' }}>
                Value
              </Typography>

              <CustomTextField variant='standard' size='small' autoComplete='off'
                               value={uniqueValuesItem.value}
                               inputProps={{ type: isNumber ? 'number' : 'string' }} 
                               onChange={(e)=>OnUniqueValueItemTextFieldChanged(e,uniqueValuesItem)}
                               sx={{ p: 0 }}/>
            </Stack>

            {/* Color editor */}

            <Stack sx={{ ml: 2 }}>
              <Typography sx={{ color: theme_textColorBlended, fontSize: '0.8rem' }}>
                Color
              </Typography>
              <MuiColorInputStyled variant='standard' size='small' format='hex' isAlphaHidden={true}
                                   //disabled={props.unique_values === undefined}
                                   value={uniqueValuesItem.color}
                                   onChange={(v,c)=>OnUniqueValuesColorChanged(v,c,uniqueValuesItem)}
                                   sx={{ width: '120px' }}/>
            </Stack>

            {/* Delete button */}

            <IconButton sx={{p: 0.3, ml: 1}} onClick={(_)=>OnUniqueValuesRemoveColorItem(uniqueValuesItem)}>
              <DeleteIcon sx={{ color: theme_textColorBlended, opacity: 0.7, p: 0.4 }}/>
            </IconButton>

            {/* Edit button (in EDIT mode) */}

            <IconButton sx={{p: 0.3, ml: 1}} onClick={(_)=>OnExitEditMode()}>
              <EditIcon sx={{ color: theme_orange, opacity: 0.7, p: 0.4 }}/>
            </IconButton>

          </Stack>                    
        )

        else // VIEW MODE
        return (
  
            <Stack key={uniqueValuesItem.id} direction='row' sx={{ my: 0, width: '100%', alignItems: 'center' }}>
              
              <Stack direction='row' sx={{ alignItems: 'center', width: '100%' }}>
                <Typography sx={{ width: /*isNumber ? '150px' :*/ '100%', fontSize: '0.9rem', color: theme_textColorMain+'A0'}}>
                  {uniqueValuesItem.value}
                </Typography>

                <Stack direction='row' sx={{ ml: 2, width: '120px', alignItems: 'center' }}>
                  <Box sx={{ bgcolor: uniqueValuesItem.color, width: '20px', height: '20px', borderRadius: 1, border: 1, borderColor: 'black' }}/>
                  <Typography sx={{ ml: 1, color: theme_textColorMain+'A0' }}>
                    {uniqueValuesItem.color}
                  </Typography>
                </Stack>
              </Stack>

              {/* Edit button (in VIEW mode) */}

              <IconButton sx={{p: 0.3, ml: 1}} onClick={(_)=>OnEnterEditMode(uniqueValuesItem)}>
                <EditIcon sx={{ color: theme_textColorBlended, opacity: 0.7, p: 0.4 }}/>
              </IconButton>

            </Stack>
        )
      })}

      <Stack direction='row' sx={{ mt: 2, alignItems: 'start', justifyContent: 'space-between', width: '100%' }}>

        {/* Button to add a new unique value item (manually) */}

        <Button variant='outlined' onClick={()=>OnUniqueValuesAddNewItem()}
                sx={{ textTransform: 'none', borderRadius: 2, px: 1, py: 0.2, fontSize: '0.7rem', opacity: 0.8 }}>
          Add a New Unique Value
        </Button>

        {/* Button to expand this rule into multiple separate rules */}

        {props.rule.geometry.unique_values.items.length >= 2
          ?
            <Button variant='outlined' onClick={()=>OnExpandRule()}
                    sx={{ textTransform: 'none', borderRadius: 2, px: 1, py: 0.2, fontSize: '0.7rem', opacity: 0.75 }}>
              <Stack direction='column' sx={{ alignItems: 'start', maxWidth: '220px' }}>
                <Typography sx={{ fontSize: '0.75rem', color: theme_textColorBlended }}>
                  Expand this rule into {props.rule.geometry.unique_values.items.length} separate rules
                </Typography>
                <Typography sx={{ fontSize: '0.6rem', color: theme_textColorMain+'85', textAlign: 'left' }}>
                  One rule is created for each item.  All settings from this rule are copied over into the new rules.
                </Typography>
              </Stack>
            </Button>
          :null
        }

      </Stack>

    </Stack>
  )
}





//-------------------------------------------------------------------------------
// Get all unique values for a layer's attribute.
//-------------------------------------------------------------------------------
export async function GetLayerAttribUniqueValues(attribute_id: number | undefined): Promise<string[] | undefined>
{
  if(!attribute_id)
  {
    Debug.error('GetAttribUniqueValues> ERROR: undefined or invalid attribute ID');
    return undefined;
  }

  // Call the server

  const server = new CallServer();
  server.Add("attribute_id", attribute_id);
  server.Add("mode", "unique");
  server.Add("max_values", UNIQUE_VALUES_MAX_ITEMS);

  //useStore.getState().store_setDeletingLayerLibraryGroup(true);  // Tell the UI we are doing work

  const result = await server.Call('get', '/attribute_info');

  //useStore.getState().store_setDeletingLayerLibraryGroup(false); // Tell the UI we are no longer doing work

  if(result.success)
  {
    Debug.log('GetAttribUniqueValues> API server call SUCCESS');
    //Debug.log('GetAttribUniqueValues> SUCCESS! data=' + JSON.stringify(result.data));
    
    const values: string[] = result.data.values;

    //Debug.log(`GetAttribUniqueValues> unique values = ${values.length}.`);
    return values;  // success
  }
  else
  {
    // Failure
    //ToastNotification('error', "Unable to get attribute unique values")
    Debug.error(`GetAttribUniqueValues> ${result.errorCode} - ${result.errorMessage}`);
    return undefined;
  }
}

//-------------------------------------------------------------------------------
// Get default color scheme: UNIQUE VALUES
//-------------------------------------------------------------------------------
export function GetDefaultLayerStyle_UniqueValues(): ILayerStyleType_UniqueValues
{
  const defaultItem: ILayerStyleType_UniqueValues =
  {
    items: []
  }

  return defaultItem;
}

//-------------------------------------------------------------------------------
// Regenerate unique values.
//-------------------------------------------------------------------------------
export async function LayerStyle_RegenerateUniqueValues(attribute_id: number, generateRandomColors: boolean): Promise<ILayerStyleType_UniqueValues | undefined>
{
  let nextID: number = 1;

  const uniqueValues: string[] | undefined = await GetLayerAttribUniqueValues(attribute_id);
  if(uniqueValues === undefined)
  {
    ToastNotification('error', 'This attribute has no unique values');
    return undefined;
  }

  if(uniqueValues.length >= UNIQUE_VALUES_MAX_ITEMS)
    ToastNotification('error', `This attribute has too many unique values - only the first ${UNIQUE_VALUES_MAX_ITEMS} are included`);

  // If we are generating random colors, we want them to be visually distict, so
  // the colors are all generated at once using a special algorithm.
  //
  // See:  https://www.npmjs.com/package/iwanthue

  let randomColors: string[] = [];
  if(generateRandomColors) 
    randomColors = iwanthue(uniqueValues.length);

  const new_unique_values_items: ILayerStyleType_UniqueValuesItem[] = [];
  for(let i=0; i < uniqueValues.length; i++)
  {
    const newItem: ILayerStyleType_UniqueValuesItem = 
    {
      id: nextID++,
      value: uniqueValues[i],
      color: generateRandomColors ? randomColors[i] : '',
    }
    new_unique_values_items.push(newItem);
  }

  const new_unique_values: ILayerStyleType_UniqueValues =
  {
    items: new_unique_values_items
  }

  return new_unique_values;
}

//-------------------------------------------------------------------------------
// Returns the MIN value of the selected NUMERICAL attribute.
//-------------------------------------------------------------------------------
function GetNumericalAttribMinValue(values: ILayerStyleType_UniqueValuesItem[]): number | undefined
{
  if(!values || values.length === 0)
    return undefined;

  let min_value: number = Number.MAX_VALUE;
  for(let i=0; i < values.length; i++)
  {
    const value_num: number = Number.parseFloat(values[i].value);
    if(value_num < min_value)
      min_value = value_num;
  }

  return min_value;
}

//-------------------------------------------------------------------------------
// Returns the MAX value of the selected NUMERICAL attribute.
//-------------------------------------------------------------------------------
function GetNumericalAttribMaxValue(values: ILayerStyleType_UniqueValuesItem[]): number | undefined
{
  if(!values || values.length === 0)
    return undefined;

  let max_value: number = Number.MIN_VALUE;
  for(let i=0; i < values.length; i++)
  {
    const value_num: number = Number.parseFloat(values[i].value);
    if(value_num > max_value)
      max_value = value_num;
  }

  return max_value;
}

//-------------------------------------------------------------------------------
// Validates the specified rule geometry UNIQUE VALUES color scheme.
//
// Works for both regular rules and the special global defaults rule.
// 
// Returns an error message if validation FAILS, or empty string if it succeeds.
//-------------------------------------------------------------------------------
export function ValidateLayerStyleRuleGeom_UniqueValues(unique_values: ILayerStyleType_UniqueValues | undefined, 
                                                        errMsgPrefix: string): string
{
  if(unique_values === undefined)
    return errMsgPrefix + 'Invalid color scheme';

  if(unique_values.items === undefined || unique_values.items.length === 0)
    return errMsgPrefix + 'No color entries defined';

  for(let i=0; i < unique_values.items.length; i++)
  {
    if(unique_values.items[i].value === undefined || unique_values.items[i].value.length === 0)
      return errMsgPrefix + `entry ${i+1} has an undefined value`;
    if(unique_values.items[i].color === undefined || unique_values.items[i].color.length === 0)
      return errMsgPrefix + `entry ${i+1} has an undefined color`;
  }

  // Success
  return '';
}

//-------------------------------------------------------------------------------
// Convert the specified UNIQUE VALUES rule into multiple rules (one rule per item).
//-------------------------------------------------------------------------------
export function OnExpandRule_UniqueValues(rule: ILayerStyleRule, nextRuleID: number): ILayerStyleRule[] | undefined
{
  if(!rule || !rule.geometry || !rule.geometry.unique_values)
  {
    ToastNotification('error','Invalid color scheme (unique values)')
    return undefined;
  }

  const items: ILayerStyleType_UniqueValuesItem[] = rule.geometry.unique_values.items;

  if(items.length === 0)
  {
    ToastNotification('error','This color scheme contains no items');
    return undefined;
  }

  if(items.length > RULE_EXPANSION_MAX_ITEMS)
  {
    ToastNotification('error',`Rule expansion is limited to a max of ${RULE_EXPANSION_MAX_ITEMS} items`);
    return undefined;
  }

  // Convert each color scheme item into a new Single-Color rule

  const new_rules: ILayerStyleRule[] = [];

  for(let i=0; i < items.length; i++)
  {
    // Create a new geometry (Single Color)

    const newRuleGeom: ILayerStyleRule_Geometry = 
    {
      ...rule.geometry,
      color_scheme: 'single color',
      attribute_id: undefined,
      attribute_type: undefined,
      single_color: 
      {
        color: items[i].color
      },
      unique_values: undefined,
      classified: undefined,
      gradient: undefined,
    }

    // Create a filter

    const newFilter: ILayerAttribExpression =
    {
      clauses: 
      [
        {
          id: 1,
          attribute_id: rule.geometry.attribute_id,
          type: rule.geometry.attribute_type,
          operator: 'is equal to',
          value: items[i].value,
        }
      ],
      operator: 'and'
    }

    // Create a new rule for this item

    const newRule: ILayerStyleRule = 
    {
      ...rule,
      id: nextRuleID++,
      description: `Unique Value:  ${items[i].value}`,
      geometry: newRuleGeom,
      filter: newFilter,
      enabled: true,
    }
    
    // Add the new rule to the list
    new_rules.push(newRule);
  }

  // Success
  return new_rules;
}